import React from 'react';
import { motion } from 'framer-motion';
import { BuildingIcon, GlobeIcon, UsersIcon, LayersIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionNav } from '../components/SectionNav';
const CHRONOLOGY = [{
  years: '2022 – Present',
  org: 'Bank of Ireland',
  seniority: 'US Head of Finance',
  city: 'New York'
}, {
  years: '2021 – 2022',
  org: 'MUFG',
  seniority: 'Advisor, Regulatory Reporting',
  city: 'New York'
}, {
  years: '2019 – 2021',
  org: 'Mizuho',
  seniority: 'Executive Director, Controllers',
  city: 'New York'
}, {
  years: '2017 – 2019',
  org: 'JPMorgan Chase',
  seniority: 'Executive Director & Controller',
  city: 'New York'
}, {
  years: '2014 – 2017',
  org: 'Deutsche Bank',
  seniority: 'SVP, Structured Reporting & Regulation W',
  city: 'New York'
}, {
  years: '2011 – 2014',
  org: 'Royal Bank of Scotland',
  seniority: 'Finance Director, Regulatory Reporting',
  city: 'New York / London'
}, {
  years: '2008 – 2011',
  org: 'CIT Group',
  seniority: 'Vice President, Financial Reporting',
  city: 'New York'
}, {
  years: '2006 – 2008',
  org: 'Barclays Bank',
  seniority: 'Manager, Financial Control',
  city: 'New York / London'
}, {
  years: '2003 – 2006',
  org: 'Merrill Lynch',
  seniority: 'Financial Accountant',
  city: 'London'
}];
const FOOTPRINT = [{
  Icon: BuildingIcon,
  value: '9',
  label: 'Tier-1 Institutions',
  note: 'Global systemically important banks and specialty finance groups.'
}, {
  Icon: GlobeIcon,
  value: '5',
  label: 'Financial Centers',
  note: 'New York, London, Tokyo, Hong Kong, and Sydney operating exposure.'
}, {
  Icon: UsersIcon,
  value: '15',
  label: 'Direct Reports',
  note: 'Largest cross-border team led, across multiple time zones.'
}, {
  Icon: LayersIcon,
  value: '4',
  label: 'Functional Domains',
  note: 'Financial control, regulatory reporting, treasury liaison, transformation.'
}];
const GEOGRAPHIES = [{
  city: 'New York',
  detail: 'Primary base. US regulatory reporting, Fed and OCC engagement, entity-level control.'
}, {
  city: 'London',
  detail: 'Early-career financial control and group reporting for UK-headquartered institutions.'
}, {
  city: 'Tokyo',
  detail: 'Head-office coordination for Japanese banking groups (MUFG, Mizuho).'
}, {
  city: 'Hong Kong',
  detail: 'Regional finance operations oversight and shared-service alignment.'
}, {
  city: 'Sydney',
  detail: 'Cross-border reporting and offshore service migration governance.'
}];
export function Experiences() {
  return <>
      <PageHeader index="02" title="Experiences" intro="The institutional footprint: where Gerry has worked, the seniority held, the geographies covered, and the shape of the career overall." data-id="element-343" data-mp="1g7d7t369ugik" />

      <section aria-labelledby="footprint" className="w-full bg-white" data-id="element-344" data-mp="1ozx3pvv6i6zp">
        <div className="mx-auto max-w-content px-6 py-20 sm:py-24 lg:px-8" data-id="element-345" data-mp="1tyrrzdbx0nr7">
          <h2 id="footprint" className="text-[11px] font-semibold uppercase tracking-widest text-navy-700" data-id="element-346" data-mp="1twnjei32n0kl">
            Institutional Footprint
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-px border border-slateink-300/70 bg-slateink-300/70 sm:grid-cols-2 lg:grid-cols-4" data-id="element-347" data-mp="28zovz0wlgj83">
            {FOOTPRINT.map((item, i) => <motion.div key={item.label} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.07
          }} className="bg-white p-8" data-id="element-348" data-mp="1z43ztje7id69" data-mpi={i}>
                <item.Icon className="h-5 w-5 text-gold-600" strokeWidth={1.5} aria-hidden="true" data-id="element-349" data-mp="2i2g9r7cxzr2w" />
                <p className="mt-6 font-serif text-4xl text-navy-900" data-id="element-350" data-mp="2e0mfphx95lbp">{item.value}</p>
                <p className="mt-2 text-[12px] font-semibold uppercase tracking-wider text-navy-700" data-id="element-351" data-mp="3akbvr1z4uq8y">
                  {item.label}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-slateink-600" data-id="element-352" data-mp="2qaanxn8aofk6">{item.note}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section aria-labelledby="chronology" className="w-full bg-slateink-100" data-id="element-353" data-mp="2v6reeo2yv60f">
        <div className="mx-auto max-w-content px-6 py-20 sm:py-24 lg:px-8" data-id="element-354" data-mp="2hb5q6j4k4q92">
          <h2 id="chronology" className="text-[11px] font-semibold uppercase tracking-widest text-navy-700" data-id="element-355" data-mp="2lbppntcls5yd">
            Career Chronology
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slateink-600" data-id="element-356" data-mp="3dyk70he4ip7m">
            A continuous progression through financial control, regulatory reporting, and
            regional finance leadership. Detailed mandates and deliverables for each senior
            role are set out in Section 07.
          </p>

          <ol className="mt-10 border-t border-slateink-300/70" data-id="element-357" data-mp="3ry3dl5radsht">
            {CHRONOLOGY.map((entry, i) => <motion.li key={entry.years + entry.org} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-30px'
          }} transition={{
            duration: 0.4,
            delay: Math.min(i, 6) * 0.05
          }} className="grid grid-cols-1 gap-2 border-b border-slateink-300/70 py-6 sm:grid-cols-12 sm:items-center sm:gap-6" data-id="element-358" data-mp="mtjimuf5jqg" data-mpi={i}>
                <span className="text-[11px] uppercase tracking-widest text-gold-600 sm:col-span-3" data-id="element-359" data-mp="12bgpxa1b82hx">
                  {entry.years}
                </span>
                <span className="font-serif text-lg text-navy-900 sm:col-span-4" data-id="element-360" data-mp="xfjkhyrl4gjx">
                  {entry.org}
                </span>
                <span className="text-sm text-slateink-600 sm:col-span-3" data-id="element-361" data-mp="17b3cwb6xwx3x">
                  {entry.seniority}
                </span>
                <span className="text-[12px] uppercase tracking-wider text-slateink-500 sm:col-span-2 sm:text-right" data-id="element-362" data-mp="2no1mybk41vpe">
                  {entry.city}
                </span>
              </motion.li>)}
          </ol>
        </div>
      </section>

      <section aria-labelledby="geographies" className="w-full bg-navy-950" data-id="element-363" data-mp="2us9sw49h2qwd">
        <div className="mx-auto max-w-content px-6 py-20 sm:py-24 lg:px-8" data-id="element-364" data-mp="2uum5e1wgo921">
          <h2 id="geographies" className="text-[11px] font-semibold uppercase tracking-widest text-gold-400" data-id="element-365" data-mp="2zsnnokm2c4cg">
            Global Operating Exposure
          </h2>
          <ul className="mt-10 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-366" data-mp="2751n7l84tftt">
            {GEOGRAPHIES.map(geo => <li key={geo.city} className="bg-navy-950 p-8" data-id="element-367" data-mp="2h1o45yxc7mqk">
                <p className="font-serif text-xl text-white" data-id="element-368" data-mp="2gzbfl2x2qe4j">{geo.city}</p>
                <p className="mt-4 text-sm leading-relaxed text-slateink-300" data-id="element-369" data-mp="2lsweqyaele5b">
                  {geo.detail}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      <SectionNav currentIndex="02" data-id="element-370" data-mp="1ta92879h37fu" />
    </>;
}